import PropTypes from 'prop-types';
import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

const BulkActionsContext = createContext(null);

export const BulkActionsProvider = ({ children }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [statusValue, setStatusValue] = useState(null);
  const [assigneeValue, setAssigneeValue] = useState(null);
  const [sprintValue, setSprintValue] = useState(null);
  const [moduleValue, setModuleValueState] = useState(undefined);
  const [isModuleSelected, setIsModuleSelected] = useState(false);

  const setModuleValue = useCallback((value) => {
    setIsModuleSelected(true);
    setModuleValueState(value);
  }, []);

  const toggleTask = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectTask = useCallback((id, isSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setStatusValue(null);
    setAssigneeValue(null);
    setSprintValue(null);
    setIsModuleSelected(false);
    setModuleValueState(undefined);
  }, []);

  const isTaskSelected = useCallback(
    id => selectedIds.has(id),
    [selectedIds],
  );

  const value = useMemo(() => ({
    selectedIds,
    selectedCount: selectedIds.size,
    hasSelection: selectedIds.size > 0,
    isTaskSelected,
    toggleTask,
    selectTask,
    clearSelection,
    statusValue,
    setStatusValue,
    assigneeValue,
    setAssigneeValue,
    sprintValue,
    setSprintValue,
    moduleValue,
    setModuleValue,
    isModuleSelected,
  }), [
    selectedIds,
    isTaskSelected,
    toggleTask,
    selectTask,
    clearSelection,
    statusValue,
    assigneeValue,
    sprintValue,
    moduleValue,
    isModuleSelected,
  ]);

  return (
    <BulkActionsContext.Provider value={value}>
      {children}
    </BulkActionsContext.Provider>
  );
};

BulkActionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useBulkActions = () => {
  const context = useContext(BulkActionsContext);
  if (!context) {
    throw new Error('useBulkActions must be used within BulkActionsProvider');
  }
  return context;
};
